'use client'

import { useMemo, useRef, useState } from 'react'
import dynamic from 'next/dynamic'

import { ArrowUpRight, MapPin } from 'lucide-react'

import { cn } from '@/lib/utils'
import { trackInteraction } from '@/components/homepage/interaction-link'
import { screenCounts, theaters } from '@/content/70mm-imax-theaters/data'

import { formatMiles, haversineMiles } from './distance'
import { useLocator } from './locator-provider'

/**
 * Named metros for the crawlable list summary — city-level searches ("imax 70mm
 * nyc", "imax 70mm chicago") are a real slice of the demand. Every metro here
 * has a screen in `theaters`; Chicago and Toronto are metro-area framing
 * (Woodridge IL, and Mississauga/Vaughan ON), which is the same convention the
 * trip links already use. Houston is deliberately absent — there is no true
 * 70mm IMAX screen in Texas outside Dallas and San Antonio.
 */
const CITY_HIGHLIGHTS =
    'New York, Los Angeles, San Francisco, Dallas, Las Vegas, Denver, and the Chicago and Toronto areas'

const ImaxMap = dynamic(() => import('./imax-map').then((m) => m.ImaxMap), {
    ssr: false,
    loading: () => (
        <div className="flex h-full w-full items-center justify-center bg-[var(--imax-smoke-50)] text-sm text-[var(--imax-fg-3)]">
            Loading map…
        </div>
    ),
})

/**
 * The store locator: crawlable theater list beside the map. The list is
 * server-rendered on first paint (SSR of this client island), so every
 * theater name and its two links are in the HTML for crawlers. Searching
 * re-sorts by straight-line distance; row and pin selection stay in sync.
 */
export const TheaterLocator = () => {
    const { origin } = useLocator()
    const [activeId, setActiveId] = useState<string | null>(null)
    const [focusRequest, setFocusRequest] = useState<{ id: string; nonce: number } | null>(null)
    const rowRefs = useRef<Record<string, HTMLDivElement | null>>({})

    const sorted = useMemo(() => {
        if (!origin) {
            return theaters
        }
        return [...theaters].sort((a, b) => haversineMiles(origin, a) - haversineMiles(origin, b))
    }, [origin])

    // On a new search, select the nearest theater so its row, pin, and the
    // route line to it are highlighted without an extra click. Adjusted during
    // render (not in an effect) by comparing against the origin we last acted
    // on — the React-recommended pattern for prop-derived state.
    const [selectedForOrigin, setSelectedForOrigin] = useState(origin)
    if (origin !== selectedForOrigin) {
        setSelectedForOrigin(origin)
        setActiveId(origin ? (sorted[0]?.id ?? null) : null)
    }

    const selectFromList = (id: string) => {
        setActiveId(id)
        setFocusRequest((prev) => ({ id, nonce: (prev?.nonce ?? 0) + 1 }))
    }

    const selectFromPin = (id: string) => {
        setActiveId(id)
        rowRefs.current[id]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }

    return (
        <section id="screens" className="mx-auto max-w-[1240px] scroll-mt-[72px] px-6 pt-14 pb-2">
            <div className="mb-[18px]">
                <span className="text-[13px] font-bold tracking-[0.08em] text-[var(--imax-blu-700)] uppercase">
                    The Theaters
                </span>
                <h2 className="mt-[10px] max-w-[720px] text-[clamp(26px,3.6vw,38px)] leading-[1.3] font-bold tracking-[-0.01em]">
                    {origin
                        ? `IMAX 70mm theaters near ${origin.label}`
                        : `All ${screenCounts.northAmerica} IMAX 70mm theater locations in North America`}
                </h2>
                <p className="mt-2 text-sm text-[var(--imax-fg-2)]">
                    {origin
                        ? `Straight-line distances from ${origin.label}. Movie tickets are booked at the theater; the trip there is on Wanderu.`
                        : `Every real 70mm IMAX screen on the continent — ${screenCounts.us} in the US · ${screenCounts.canada} in Canada — including ${CITY_HIGHLIGHTS}. Search above to find the 70mm IMAX theater near you.`}
                </p>
            </div>

            <div className="imax-locator-grid grid grid-cols-[440px_1fr] overflow-hidden rounded-lg border border-[var(--imax-border)] bg-white shadow-[var(--imax-shadow-3)] max-[900px]:grid-cols-1">
                {/* Crawlable theater list */}
                <div className="imax-theater-list max-h-[620px] overflow-x-hidden overflow-y-auto border-r border-[var(--imax-border)] max-[900px]:max-h-[420px] max-[900px]:border-r-0 max-[900px]:border-b">
                    {sorted.map((theater, index) => {
                        const active = activeId === theater.id
                        const ticketsDomain = new URL(theater.ticketsUrl).hostname
                        return (
                            <div
                                key={theater.id}
                                ref={(el) => {
                                    rowRefs.current[theater.id] = el
                                }}
                                onClick={() => {
                                    selectFromList(theater.id)
                                }}
                                className={cn(
                                    'cursor-pointer border-b border-l-[3px] border-b-[var(--imax-border)] px-5 py-[18px] transition-colors duration-150',
                                    active
                                        ? 'border-l-[var(--imax-blu-700)] bg-[var(--imax-blu-25)]'
                                        : 'border-l-transparent bg-white hover:bg-[var(--imax-smoke-25)]',
                                )}
                            >
                                <h3 className="text-base leading-[1.25] font-bold">
                                    {theater.name}
                                </h3>
                                <p className="mt-[3px] text-sm text-[var(--imax-fg-2)]">
                                    {theater.address
                                        ? `${theater.address}, ${theater.city}`
                                        : theater.city}
                                </p>
                                {origin && (
                                    <div className="mt-2 flex items-center gap-[6px] text-[13px] text-[var(--imax-fg-3)]">
                                        <MapPin className="h-[13px] w-[13px]" aria-hidden="true" />~
                                        {formatMiles(haversineMiles(origin, theater))} away
                                    </div>
                                )}
                                {/*
                                 * Fares first, filled: the trip to the theater is
                                 * what Wanderu monetizes, so it leads. The box
                                 * office is the supporting action — people who came
                                 * to buy a movie ticket still find it, but it no
                                 * longer outranks the fare link visually.
                                 */}
                                <div className="mt-3 flex flex-wrap gap-2">
                                    <a
                                        href={theater.tripUrl}
                                        target="_blank"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            trackInteraction('Landing Page Interacted', {
                                                interaction_type: 'content_link_clicked',
                                                content_name: 'fares_to_get_there',
                                                link_url: theater.tripUrl,
                                                position: index + 1,
                                            })
                                        }}
                                        className="inline-flex items-center gap-[7px] rounded-[3px] bg-[var(--imax-primary)] px-[13px] py-2 text-[13px] font-bold text-white transition-colors duration-150 hover:bg-[var(--imax-primary-hover)]"
                                    >
                                        Fares to Get There
                                    </a>
                                    <a
                                        href={theater.ticketsUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            trackInteraction('Landing Page Interacted', {
                                                interaction_type: 'outbound_link_clicked',
                                                link_url: theater.ticketsUrl,
                                                link_domain: ticketsDomain,
                                                is_outbound: true,
                                            })
                                        }}
                                        className="inline-flex items-center gap-[7px] rounded-[3px] border border-[var(--imax-border-dark)] px-[13px] py-[7px] text-[13px] font-bold text-[var(--imax-fg-2)] transition-colors duration-150 hover:bg-[var(--imax-smoke-50)]"
                                    >
                                        Showtimes &amp; Tickets
                                        <ArrowUpRight className="h-3 w-3" aria-hidden="true" />
                                    </a>
                                </div>
                            </div>
                        )
                    })}
                </div>
                {/* Map pane */}
                <div className="h-[620px] max-[900px]:h-[380px]">
                    <ImaxMap
                        theaters={theaters}
                        activeId={activeId}
                        focusRequest={focusRequest}
                        onPinSelect={selectFromPin}
                        origin={origin}
                    />
                </div>
            </div>
        </section>
    )
}
